import { Component, OnInit, inject, effect, input, computed, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TreeSelectModule } from 'primeng/treeselect';
import { TranslateModule } from '@ngx-translate/core';
import { TreeNode } from 'primeng/api';
import { UserAccessService } from '../../../features/home/services/user-access.service';
import { DocumentSearchService } from '../../../features/home/services/document-search.service';

const STORAGE_KEY = 'library-class-selection';

interface StoredSelection {
    libraryId: string;
    classId: string;
    libraryName: string;
    className: string;
    timestamp: number;
}

@Component({
    selector: 'app-library-class-selector',
    standalone: true,
    imports: [CommonModule, FormsModule, TreeSelectModule, TranslateModule],
    templateUrl: './library-class-selector.component.html',
    styleUrls: ['./library-class-selector.component.scss']
})
export class LibraryClassSelectorComponent implements OnInit {

    @Output() userSelectionChange = new EventEmitter<void>();
    
    // Inputs for customization
    showLabel = input<boolean>(true);
    size = input<'small' | 'large'>('small');
    variant = input<'default' | 'topbar'>('default');
    customStyleClass = input<string>('');

    // State
    treeNodes: TreeNode[] = [];
    selectedNode: TreeNode | null = null;

    // Inject services
    public userAccessService = inject(UserAccessService);
    private documentSearchService = inject(DocumentSearchService);

    // Computed for containerStyleClass
    containerStyleClass = computed(() => {
        return this.customStyleClass() || 'min-w-[280px]';
    });

    isLoading = computed(() => this.userAccessService.isLoading());

    constructor() {
        // React to changes in user access data
        effect(() => {
            const access = this.userAccessService.userAccess();
            if (access && access.length > 0) {
                this.buildTreeNodes();
                this.loadSelectionFromStorage();
            }
        });

        // React to selection changes from other components
        effect(() => {
            const selection = this.userAccessService.currentSelection();
            this.syncSelectedNode();
        });
    }

    ngOnInit() {
        // Load user access if not already loaded
        if (this.userAccessService.userAccess().length === 0) {
            this.userAccessService.loadUserAccess();
        } else {
            this.buildTreeNodes();
            this.loadSelectionFromStorage();
        }
    }

    /**
     * Build tree structure from user access data
     */
    private buildTreeNodes() {
        const access = this.userAccessService.userAccess();
        
        this.treeNodes = access
            .filter(library => library.libraryId && library.classes && library.classes.length > 0)
            .map(library => {
                const libraryNode: TreeNode = {
                    key: library.libraryId!,
                    label: library.libraryName || library.libraryId || 'Unknown Library',
                    data: {
                        type: 'library',
                        libraryId: library.libraryId
                    },
                    icon: 'pi pi-database text-blue-500',
                    styleClass: 'library-node',
                    selectable: false,
                    children: library.classes!
                        .filter(cls => cls.classId)
                        .map(cls => ({
                            key: `${library.libraryId}|${cls.classId}`,
                            label: cls.className || cls.classId || 'Unknown Class',
                            data: {
                                type: 'class',
                                libraryId: library.libraryId,
                                classId: cls.classId,
                                libraryName: library.libraryName,
                                className: cls.className
                            },
                            icon: 'pi pi-bookmark text-green-500',
                            styleClass: 'class-node',
                            selectable: true,
                            leaf: true
                        }))
                        .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
                };
                return libraryNode;
            })
            .filter(lib => lib.children && lib.children.length > 0)
            .sort((a, b) => (a.label || '').localeCompare(b.label || ''));

        console.log('[LibraryClassSelector] Tree nodes built:', this.treeNodes);
    }

   /**
     * Load selection from localStorage and apply it, or apply default
     */
    private loadSelectionFromStorage() {
        // Ensure tree nodes are built before trying to apply a default
        if (this.treeNodes.length === 0) {
            console.log('[LibraryClassSelector] Tree nodes not ready, skipping selection load.');
            return;
        }

        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (!stored) {
                console.log('[LibraryClassSelector] No stored selection found. Applying default.');
                this.applyDefaultSelection(); 
                return;
            }

            const selection: StoredSelection = JSON.parse(stored);
            
            // Validate that the stored selection still exists in available options
            if (this.isSelectionValid(selection.libraryId, selection.classId)) {
                console.log('[LibraryClassSelector] Loading stored selection:', selection);
                this.userAccessService.setSelection(selection.libraryId, selection.classId);
                this.syncSelectedNode();
            } else {
                console.warn('[LibraryClassSelector] Stored selection no longer valid. Applying default.');
                this.clearStoredSelection();
                this.applyDefaultSelection(); 
            }
        } catch (error) {
            console.error('[LibraryClassSelector] Error loading stored selection. Applying default.', error);
            this.clearStoredSelection();
            this.applyDefaultSelection(); 
        }
    }

    /**
     * Validate if a selection exists in the available options
     */
    private isSelectionValid(libraryId: string, classId: string): boolean {
        for (const libraryNode of this.treeNodes) {
            if (libraryNode.key === libraryId && libraryNode.children) {
                const classNode = libraryNode.children.find(
                    child => child.data?.classId === classId
                );
                if (classNode) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * Save selection to localStorage
     */
    private saveSelectionToStorage(libraryId: string, classId: string, libraryName: string, className: string) {
        try {
            const selection: StoredSelection = {
                libraryId,
                classId,
                libraryName,
                className,
                timestamp: Date.now()
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
            console.log('[LibraryClassSelector] Selection saved to storage:', selection);
        } catch (error) {
            console.error('[LibraryClassSelector] Error saving selection to storage:', error);
        }
    }

    /**
     * Clear stored selection from localStorage
     */
    private clearStoredSelection() {
        try {
            localStorage.removeItem(STORAGE_KEY);
            console.log('[LibraryClassSelector] Stored selection cleared');
        } catch (error) {
            console.error('[LibraryClassSelector] Error clearing stored selection:', error);
        }
    }

    /**
     * Sync the selected node with the UserAccessService state
     */
    private syncSelectedNode() {
        const selection = this.userAccessService.currentSelection();
        
        if (!selection.libraryId || !selection.classId) {
            this.selectedNode = null;
            return;
        }

        const key = `${selection.libraryId}|${selection.classId}`;
        
        for (const libraryNode of this.treeNodes) {
            if (libraryNode.children) {
                const classNode = libraryNode.children.find(child => child.key === key);
                if (classNode) {
                    this.selectedNode = classNode;
                    return;
                }
            }
        }

        console.warn('[LibraryClassSelector] Could not find node for selection:', selection);
        this.selectedNode = null;
    }

    /**
     * Handle node selection
     */
    onNodeSelect(event: any) {
        const node = event.node as TreeNode;
        
        if (!node || !node.data || node.data.type !== 'class') {
            console.warn('[LibraryClassSelector] Invalid node selected:', node);
            return;
        }

        const { libraryId, classId, libraryName, className } = node.data;
        
        if (!libraryId || !classId) {
            console.error('[LibraryClassSelector] Missing libraryId or classId in node data');
            return;
        }

        console.log('[LibraryClassSelector] Node selected:', { libraryId, classId });
        
        // Save to localStorage
        this.saveSelectionToStorage(libraryId, classId, libraryName, className);

        // Emit event to notify parent components
        this.userSelectionChange.emit();
        
        // Update the selection in the service
        this.userAccessService.setSelection(libraryId, classId);
    }

    /**
     * Prevent deselection by immediately re-selecting the node.
     */
    onNodeUnselect(event: any) {
        console.warn('[LibraryClassSelector] Deselection prevented.');
        
        // The event.node is the node that was just unselected.
        // We need to re-assign it back to selectedNode.
        // We use setTimeout to ensure this runs just after the
        // current event cycle, preventing any conflicts.
        setTimeout(() => {
            this.selectedNode = event.node;
        }, 0);
    }

/**
     * Selects the first available library/class combination.
     * Used as a fallback when no valid selection is loaded from storage.
     */
    private applyDefaultSelection() {
        if (this.treeNodes.length === 0) { // Simplified the guard
            console.warn('[LibraryClassSelector] No default selection available to apply.');
            return;
        }

        try {
            const firstLibraryNode = this.treeNodes[0];
            // Use optional chaining here
            const firstClassNode = firstLibraryNode.children?.[0]; 
            
            // This check now correctly handles if firstClassNode is undefined
            if (!firstClassNode || !firstClassNode.data) { 
                console.error('[LibraryClassSelector] First class node is invalid or has no data.');
                return;
            }

            const { libraryId, classId, libraryName, className } = firstClassNode.data;

            if (libraryId && classId) {
                console.log('[LibraryClassSelector] Applying default selection:', { libraryId, classId });
                // Save this new default to storage
                this.saveSelectionToStorage(libraryId, classId, libraryName, className);
                // Set it in the service
                this.userAccessService.setSelection(libraryId, classId);
                // The effect will catch this change and call syncSelectedNode()
            }
        } catch (error) {
            console.error('[LibraryClassSelector] Error applying default selection:', error);
        }
    }
}